import React from 'react';
import { FiHome, FiGlobe, FiUser, FiMail, FiPhone, FiCreditCard, FiMapPin, FiLayers, FiAlertCircle, FiFileText, FiUsers } from 'react-icons/fi';

export function BusinessInfoStep({ formData, onChange, errors = {} }) {
  // Define TNS-specific business sectors
  const businessSectors = [
    { id: 'retail', name: 'Retail' },
    { id: 'fuel', name: 'Fuel & Convenience' },
    { id: 'parking', name: 'Parking' },
    { id: 'vending', name: 'Vending' },
    { id: 'ev', name: 'EV Charging' },
    { id: 'hospitality', name: 'Hospitality' },
    { id: 'ticketing and entertainment', name: 'Ticketing & Entertainment' },
    { id: 'transportation', name: 'Transportation' },
    { id: 'unattended other', name: 'Unattended (other)' },
    { id: 'other', name: 'Other' }
  ];

  // Define counties/countries for each region
  const regionCountries = {
    usa: [
      { id: 'United States', name: 'United States' },
    ],
    eu: [
        { id: 'United Kingdom', name: 'United Kingdom' },
        { id: 'Ireland', name: 'Ireland' },
        { id: 'France', name: 'France' },
        { id: 'Spain', name: 'Spain' },
        { id: 'Italy', name: 'Italy' },
        { id: 'Germany', name: 'Germany' }
    ],
    apac: [
      { id: 'Australia', name: 'Australia' },
      { id: 'New Zealand', name: 'New Zealand' },
      { id: 'Malaysia', name: 'Malaysia' },
    ],
    region4: [
      { id: 'Brazil', name: 'Brazil' }
    ]
  };

  // Get countries for selected region
  const availableCountries = formData.region ? regionCountries[formData.region] || [] : [];

  // Reset country when region changes
  const handleRegionChange = (region) => {
    onChange('region', region);
    // Clear country selection when region changes
    if (formData.country) {
      onChange('country', '');
    }
  };

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {/* TNS Region Selection */}
      <div className="space-y-3">
        <label className="flex items-center text-sm font-medium text-gray-700">
          <FiMapPin className="w-4 h-4 mr-2" />
          TNS Region <span className="text-red-500 ml-1">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className={`flex items-center p-3 border ${errors.region ? 'border-red-500' : 'border-gray-300'} rounded-md cursor-pointer hover:bg-blue-50`}>
            <input
              type="radio"
              name="region"
              value="usa"
              checked={formData.region === 'usa'}
              onChange={() => handleRegionChange('usa')}
              className={`h-4 w-4 ${errors.region ? 'text-red-600 focus:ring-red-500' : 'text-blue-600 focus:ring-blue-500'} mr-2`}
            />
            <span>Region 1 - United States</span>
          </label>
          
          <label className={`flex items-center p-3 border ${errors.region ? 'border-red-500' : 'border-gray-300'} rounded-md cursor-pointer hover:bg-blue-50`}>
            <input
              type="radio"
              name="region"
              value="eu"
              checked={formData.region === 'eu'}
              onChange={() => handleRegionChange('eu')}
              className={`h-4 w-4 ${errors.region ? 'text-red-600 focus:ring-red-500' : 'text-blue-600 focus:ring-blue-500'} mr-2`}
            />
            <span>Region 2 - UK, Ireland and MLE</span>
          </label>
          
          <label className={`flex items-center p-3 border ${errors.region ? 'border-red-500' : 'border-gray-300'} rounded-md cursor-pointer hover:bg-blue-50`}>
            <input
              type="radio"
              name="region"
              value="apac"
              checked={formData.region === 'apac'}
              onChange={() => handleRegionChange('apac')}
              className={`h-4 w-4 ${errors.region ? 'text-red-600 focus:ring-red-500' : 'text-blue-600 focus:ring-blue-500'} mr-2`}
            />
            <span>Region 3 - APAC</span>
          </label>
          
          <label className={`flex items-center p-3 border ${errors.region ? 'border-red-500' : 'border-gray-300'} rounded-md cursor-pointer hover:bg-blue-50`}>
            <input
              type="radio"
              name="region"
              value="region4"
              checked={formData.region === 'region4'}
              onChange={() => handleRegionChange('region4')}
              className={`h-4 w-4 ${errors.region ? 'text-red-600 focus:ring-red-500' : 'text-blue-600 focus:ring-blue-500'} mr-2`}
            />
            <span>Region 4 - Brazil</span>
          </label>
        </div>
        {errors.region && (
          <div className="text-red-500 text-sm flex items-center mt-1">
            <FiAlertCircle className="w-4 h-4 mr-1" />
            {errors.region}
          </div>
        )}
      </div>

      {/* Country Selection - appears when region is selected */}
      {formData.region && availableCountries.length > 0 && (
        <div className="space-y-2">
          <label className="flex items-center text-sm font-medium text-gray-700">
            <FiMapPin className="w-4 h-4 mr-2" />
            Country <span className="text-red-500 ml-1">*</span>
          </label>
          <select
            value={formData.country || ''}
            onChange={(e) => onChange('country', e.target.value)}
            className={`w-full px-4 py-2 border ${errors.country ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-blue-500 focus:border-blue-500`}
          >
            <option value="" disabled>Select country</option>
            {availableCountries.map(country => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
          {errors.country && (
            <div className="text-red-500 text-sm flex items-center mt-1">
              <FiAlertCircle className="w-4 h-4 mr-1" />
              {errors.country}
            </div>
          )}
        </div>
      )}

      {/* Business Sector Selection */}
      <div className="space-y-2">
        <label className="flex items-center text-sm font-medium text-gray-700">
          <FiLayers className="w-4 h-4 mr-2" />
          Business Sector <span className="text-red-500 ml-1">*</span>
        </label>
        <select
          value={formData.businessSector || ''}
          onChange={(e) => onChange('businessSector', e.target.value)}
          className={`w-full px-4 py-2 border ${errors.businessSector ? 'border-red-500' : 'border-gray-300'} rounded-md focus:ring-blue-500 focus:border-blue-500`}
        >
          <option value="" disabled>Select business sector</option>
          {businessSectors.map(sector => (
            <option key={sector.id} value={sector.id}>
              {sector.name}
            </option>
          ))}
        </select>
        {errors.businessSector && (
          <div className="text-red-500 text-sm flex items-center mt-1">
            <FiAlertCircle className="w-4 h-4 mr-1" />
            {errors.businessSector}
          </div>
        )}
      </div>

      {/* Opportunity Background */}
      <div className="space-y-2">
        <label className="flex items-center text-sm font-medium text-gray-700">
          <FiFileText className="w-4 h-4 mr-2" />
          Opportunity Background (Optional)
        </label>
        <textarea
          value={formData.opportunityBackground || ''}
          onChange={(e) => onChange('opportunityBackground', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 resize-vertical"
          placeholder="Describe the opportunity background, tender details, project scope, or any relevant context..."
        />
      </div>

      {/* Current Acquirers/Processors */}
      <div className="space-y-2">
        <label className="flex items-center text-sm font-medium text-gray-700">
          <FiUsers className="w-4 h-4 mr-2" />
          Current Acquirers/Processors (Optional)
        </label>
        <textarea
          value={formData.currentAcquirers || ''}
          onChange={(e) => onChange('currentAcquirers', e.target.value)}
          rows={3}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 resize-vertical"
          placeholder="List current payment processors, acquirers, or any plans for changes. Include reasons for change if applicable..."
        />
      </div>

      {/* Transaction Volume */}
      <div className="space-y-2">
        <label className="flex items-center text-sm font-medium text-gray-700">
          <FiCreditCard className="w-4 h-4 mr-2" />
          Expected Monthly Volume
        </label>
        <input
          type="text"
          value={formData.expectedMonthlyVolume || ''}
          onChange={(e) => onChange('expectedMonthlyVolume', e.target.value)}
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g. 50,000 transactions"
        />
      </div>

      {/* Optional Business Details Section */}
      <div className="pt-3 border-t border-gray-300">
        <details>
          <summary className="text-sm font-medium text-blue-600 cursor-pointer">
            Additional Business Details (Optional)
          </summary>
          
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FiHome className="w-4 h-4 mr-2" />
                Business Name
              </label>
              <input
                type="text"
                value={formData.businessName || ''}
                onChange={(e) => onChange('businessName', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter business name"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FiGlobe className="w-4 h-4 mr-2" />
                Website
              </label>
              <input
                type="url"
                value={formData.website || ''}
                onChange={(e) => onChange('website', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://example.com"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FiUser className="w-4 h-4 mr-2" />
                Contact Name
              </label>
              <input
                type="text"
                value={formData.contactName || ''}
                onChange={(e) => onChange('contactName', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter contact name"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FiMail className="w-4 h-4 mr-2" />
                Contact Email
              </label>
              <input
                type="email"
                value={formData.contactEmail || ''}
                onChange={(e) => onChange('contactEmail', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="contact@example.com"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center text-sm font-medium text-gray-700">
                <FiPhone className="w-4 h-4 mr-2" />
                Contact Phone
              </label>
              <input
                type="tel"
                value={formData.contactPhone || ''}
                onChange={(e) => onChange('contactPhone', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter phone number"
              />
            </div>
          </div>
        </details>
      </div>
    </div>
  );
}